import { useInfiniteQuery } from '@tanstack/react-query';

import { CraveErrorCode } from '@/enums/errorCodes';
import { Screen, SessionContext } from '@/services/graphQL/api/generated/types_web';
import { getSystem, isLowerPerformingPlatform } from '@/system';
import { getCraveError } from '@/utils/errors';

import { requestGraphQL } from '../client';
import { queryKeys } from '../queryKeys';
import {
  ScreenResponse,
  type ScreenViewData,
  getStableDcoCorrelationId,
  selectScreenQueryData,
} from '../selectors/screenTransforms';
import { gql } from '../utils';
import {
  itemFragments,
  itemFragmentsWithoutLinkImageItem,
  liveItemFragment,
} from './itemFragments';

/** For platforms that render tiles straight from the screen response. */
const fullContainerItems = `
                ...PromotionalTeaserItem
                ...MediaMetadataItem
                ...LinkImageItem
                ...ContentMetadataItem
                ...CollectionMetadataItem
                ...LinkTextItem
                ...LiveChannelItem
                __typename`;

/**
 * For platforms where `requiresDynamicFetching` re-fetches every non-SUBNAV container
 * through `containerQuery`, so tile data in the screen response is thrown away.
 *
 * `MediaMetadata` is deliberately absent and `LinkImage` / `CollectionMetadata` are
 * reduced to `id`. `supportsAtLeastOneItem` only needs `__typename` to decide whether
 * a container is renderable. The types below stay whole because they are *not*
 * re-fetched: the teaser is read off the screen response by `getPromoTeaser`, and
 * SUBNAV containers (`LinkText`) are excluded from dynamic fetching.
 */
const skeletonContainerItems = `
                ...PromotionalTeaserItem
                ...ContentMetadataItem
                ...LinkTextItem
                ...LiveChannelItem
                ... on LinkImage {
                  id
                }
                ... on CollectionMetadata {
                  id
                }
                __typename`;

const buildScreenOperation = (containerItems: string) => `
  query screen(
    $sessionContext: SessionContext!
    $id: String!
    $limit: Int
    $cursor: String
  ) {
    screen(sessionContext: $sessionContext, id: $id) {
      title
      screenContentsPage(limit: $limit, cursor: $cursor) {
        cursor
        dcoCorrelationId
        screenContents {
          ...LiveItem
          ... on Container {
            id
            containerDisplayTitle: displayTitle
            displayType
            style
            containerItemsPage {
              cursor
              items {${containerItems}
              }
              totalItemsCount
            }
            configProperties {
              disableBadges
              disableCount
              disableDisplayTitle
              hideMediaTitle
              featuredRowConfig {
                backgroundColor
                backgroundImages {
                  ...EditorialImageFields
                }
                featuredBlock {
                  cta {
                    ...LinkButtonItem
                  }
                  headline
                  logo {
                    ...ImageFragment
                  }
                  oneLiner
                }
                featuredLink {
                  ...LinkImageFields
                }
              }
            }
            __typename
          }
          ... on AdElement {
            adType
          }
          ... on LinkImage {
            displayTitle
            ...LinkImageFields
          }
        }
      }
      navigation {
        id
        displayTitle
        logo {
          url
        }
        navigationLinks {
          ...LinkTextItem
        }
      }
    }
  }
`;

/** Every screen, on platforms that render tiles straight from the screen response. */
export const screenQuery = gql`
  ${buildScreenOperation(fullContainerItems)}
  ${itemFragments}
  ${liveItemFragment}
`;

/**
 * Screen query without row-item payload, for platforms that re-fetch every container
 * through `containerQuery` and would otherwise download tile data twice
 */
export const screenSkeletonQuery = gql`
  ${buildScreenOperation(skeletonContainerItems)}
  ${itemFragmentsWithoutLinkImageItem}
  ${liveItemFragment}
`;

/**
 * Shared with `prefetchScreen` so both prime the same cache entry with the same
 * document.
 */
export const usesSkeletonScreenQuery = (): boolean =>
  isLowerPerformingPlatform(getSystem().id);

/** Cursor for the next screen page and the DCO correlation id RTE expects back on it. */
type ScreenPageParam = {
  cursor: string;
  dcoCorrelationId: string | null;
};

/** Next page cursor, carrying the first DCO correlation id RTE returned for this screen. */
export const getNextScreenPageParam = (
  lastPage: ScreenResponse,
  allPages: ScreenResponse[]
): ScreenPageParam | undefined => {
  const cursor = lastPage.screen?.screenContentsPage?.cursor;
  if (cursor == null) return undefined;

  return { cursor, dcoCorrelationId: getStableDcoCorrelationId(allPages) };
};

type UseRTEScreenResult = {
  loading: boolean;
  error: Error | undefined;
  screen: Screen | undefined;
  data?: ScreenViewData;
  refetch: () => Promise<unknown>;
  hasNextPage?: boolean;
  fetchNextPage?: () => Promise<unknown>;
  isFetchingNextPage?: boolean;
};

export type ScreenVariant = 'home' | 'live' | 'movies' | 'curated' | 'sports' | 'kids';

/**
 * Number of rows fetched in the first home-screen page for faster TTI.
 * Covers the PromotionalTeaser (if present) and the first content row,
 * matching the requirement: "initial = promo teaser + first row".
 */
export const HOME_SCREEN_INITIAL_LIMIT = 2;

export const useRTEScreen = (
  sessionContext: SessionContext,
  id: String,
  limit?: number,
  cursor?: null,
  screenVariant?: ScreenVariant,
  enabled = true
): UseRTEScreenResult => {
  const isHomeScreen = screenVariant === 'home';
  const isLiveScreen = screenVariant === 'live';
  const screenId = String(id);
  const effectiveLimit = limit ?? 10;

  const query = usesSkeletonScreenQuery() ? screenSkeletonQuery : screenQuery;
  // Home screen uses a variant-specific key so the prefetch in prefetchScreen.ts
  // lands in the exact same cache entry.
  const queryKey = isHomeScreen
    ? queryKeys.homeScreen(sessionContext, screenId)
    : [...queryKeys.screen(sessionContext, screenId), effectiveLimit, cursor];

  const {
    data,
    error,
    isLoading,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    refetch,
  } = useInfiniteQuery<
    ScreenResponse,
    Error,
    ScreenViewData,
    readonly unknown[],
    ScreenPageParam | null
  >({
    queryKey: queryKey as readonly unknown[],
    queryFn: ({ pageParam }) =>
      requestGraphQL<ScreenResponse>(
        query,
        {
          sessionContext,
          id: screenId,
          // Home screen: fetch only a few rows initially for faster first paint,
          // then use the full limit for all subsequent (deferred) pages.
          limit: isHomeScreen && !pageParam ? HOME_SCREEN_INITIAL_LIMIT : effectiveLimit,
          cursor: pageParam?.cursor ?? null,
        },
        pageParam?.dcoCorrelationId
          ? { 'x-dco-correlation-id': pageParam.dcoCorrelationId }
          : undefined,
        {
          // Screen pagination can fail one row while still returning valid
          // rotators for the page. Consume partial data so pagination continues.
          allowPartialData: true,
        }
      ),
    initialPageParam: cursor ?? null,
    select: selectScreenQueryData,
    getNextPageParam: getNextScreenPageParam,
    refetchOnMount: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  if (error && isLiveScreen)
    throw getCraveError(
      CraveErrorCode.LIVE_CHANNELS_UNAVAILABLE_1003,
      sessionContext.userLanguage
    );

  if (!data && error)
    throw getCraveError(
      CraveErrorCode.SCREEN_NOT_READY_1001,
      sessionContext.userLanguage
    );

  return {
    loading: isLoading,
    error: error ?? undefined,
    screen: data?.screen,
    refetch,
    data,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
  };
};
